/**
 * CORS 이미지 로더 + 모듈 캐시 (BOOTH-103 부스 워터마크).
 *
 * `loadImageCors`: crossOrigin="anonymous" 를 src 지정 **전**에 설정해 S3 CORS 이미지를 캔버스에 쓸 수 있게 한다.
 * `getWatermarkImage`: 같은 URL 은 한 번만 로드하는 캐시 래퍼. 성공만 캐시하며 실패 시 캐시 항목을 지워 재시도를 허용한다.
 * 로드 실패가 손님 플로우를 막지 않도록 — 상위 호출자가 null 을 받으면 워터마크 없이 진행한다.
 */

import { trackError } from "../telemetry/activity";

/** CORS 이미지 로드 — S3 CORS 객체를 캔버스에 쓰려면 crossOrigin 을 src 전에 설정해야 한다. */
export function loadImageCors(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous"; // src 지정 전에 설정 — 순서 중요
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("이미지 로드 실패"));
    img.src = src;
  });
}

/** 모듈 레벨 캐시 — Promise 단위로 저장해 동시 중복 요청을 막는다. 성공만 캐시하며 실패 항목은 삭제해 재시도를 허용한다. */
const _cache = new Map<string, Promise<HTMLImageElement | null>>();

/**
 * 워터마크 이미지 로드 (캐시).
 * - `url === null` → 즉시 `null` resolve
 * - 같은 URL 은 한 번만 로드, Promise 를 캐시해 재요청에 즉시 반환
 * - 실패 시 캐시 항목을 삭제해 다음 호출이 재시도할 수 있도록 한다 (일시적 네트워크 오류 대응)
 */
export function getWatermarkImage(url: string | null): Promise<HTMLImageElement | null> {
  if (url === null) return Promise.resolve(null);
  const cached = _cache.get(url);
  if (cached !== undefined) return cached;
  const p = loadImageCors(url).then(
    (img) => img as HTMLImageElement | null,
    (err) => {
      console.warn("[watermark] PNG 로드 실패 — 워터마크 없이 진행", url, err);
      trackError("watermark_load_failed", err);
      // 실패는 캐시하지 않아 다음 refresh() / 호출이 재시도할 수 있다
      _cache.delete(url);
      return null as HTMLImageElement | null;
    }
  );
  _cache.set(url, p);
  return p;
}
